import { getSiteConfig } from '@shared/config';
import { NumberFormats, type useCurrencyFormatter } from '@shared/utils';
import { z } from 'zod';
import { isValidAccountHolderName, isValidBankDisplayName } from '../paymentFieldPatterns';
import { bankCodeDigitCount } from './utils/bankCodeFormat';

type CreateWithdrawalSchemaOptions = {
  t: (key: string, values?: Record<string, string | number>) => string;
  formatCurrency: ReturnType<typeof useCurrencyFormatter>;
  /** Withdrawable balance in minor units. */
  withdrawableBalance: number;
  /** Smallest withdrawal the cashier allows. */
  minWithdrawalAmount: number;
};

/**
 * Builds the withdrawal form schema for the current market.
 */
function createWithdrawalSchema({ t, formatCurrency, withdrawableBalance, minWithdrawalAmount }: CreateWithdrawalSchemaOptions) {
  const config = getSiteConfig().WITHDRAWAL;
  const { minDigits, maxDigits } = config.accountNumber;
  const accountNumberPattern = new RegExp(`^\\d{${minDigits},${maxDigits}}$`);
  const bankCodeDigits = bankCodeDigitCount(config.bankCode.groupSizes);

  const bankSchema = config.bankFields.includes('bankName')
    ? z
        .string()
        .optional()
        .refine(val => !!val, t('bank_details.bank.required_error'))
        .refine(val => !val || isValidBankDisplayName(val), t('bank_details.bank.invalid_error'))
    : z.string().optional();

  return z.object({
    amountStep: z.object({
      amount: z
        .string()
        .min(1, t('amount.required_error'))
        .refine(val => !Number.isNaN(Number(val)), t('amount.invalid_error'))
        .refine(val => Number(val) > 0, t('amount.minimum_error'))
        .refine(
          val => Math.round(Number(val) * 100) >= minWithdrawalAmount,
          t('amount.min_withdrawal_error', {
            amount: formatCurrency(minWithdrawalAmount).format(NumberFormats.WITH_DECIMALS).get(),
          })
        )
        .refine(
          val => Math.round(Number(val) * 100) <= withdrawableBalance,
          t('amount.balance_exceeded', { balance: formatCurrency(withdrawableBalance).format(NumberFormats.WITH_DECIMALS).get() })
        ),
      paymentMethod: z.string().min(1, t('payment_method.required_error')),
    }),
    methodStep: z.object({
      accountNumber: z
        .string()
        .min(1, t('bank_details.account_number.required_error'))
        .refine(val => accountNumberPattern.test(val.replace(/\s/g, '')), t('bank_details.account_number.invalid_error')),
      bankCode: z
        .string()
        .min(1, t('bank_details.bank_code.required_error'))
        .refine(val => val.replace(/\D/g, '').length === bankCodeDigits, t('bank_details.bank_code.invalid_error')),
      bank: bankSchema,
      accountName: z
        .string()
        .min(1, t('bank_details.account_name.required_error'))
        .refine(val => isValidAccountHolderName(val), t('bank_details.account_name.invalid_error')),
    }),
  });
}

export { createWithdrawalSchema };
export type { CreateWithdrawalSchemaOptions };
